import { RichEditorProps, RichEditorEditorProps, HtmlContent } from './RichEditor.types';

/**
 * HTML 콘텐츠 추출 함수
 * 
 * @param content 컨텐츠 (문자열 또는 객체)
 * @returns 추출된 HTML 문자열
 */
export function extractHtmlContent(content: HtmlContent): string {
  if (typeof content === 'string') {
    return content;
  } 
  
  if (typeof content === 'object' && content !== null) {
    let htmlContent = '';
    
    // html 속성이 있는 경우
    if ('html' in content && content.html) {
      htmlContent = content.html;
    }
    
    // cssStyles 속성이 있는 경우 합치기
    if ('cssStyles' in content && content.cssStyles) {
      console.log('[extractHtmlContent] CSS 스타일 발견, 합치기');
      htmlContent = `<style>\n${content.cssStyles}\n</style>\n\n${htmlContent}`;
    }
    
    return htmlContent;
  }
  
  return '';
}

/**
 * HTML 내용이 유효한지 확인하는 함수
 * 
 * @param html HTML 콘텐츠
 * @returns 유효성 여부
 */
export function isValidHTML(html: HtmlContent): boolean {
  if (!html) return false;
  
  // 객체인 경우 html 속성 확인
  if (typeof html === 'object' && html !== null) {
    if ('html' in html && html.html) {
      return typeof html.html === 'string' && html.html.length > 0;
    }
    return false;
  }
  
  // 문자열인 경우 직접 확인
  if (typeof html === 'string') {
    try {
      // 간단한 검사
      return html.trim().length > 0;
    } catch (error) {
      console.error('HTML 유효성 검사 오류:', error);
      return false;
    }
  }
  
  return false;
}

/**
 * 원본 props를 리치 에디터 컴포넌트에 맞게 변환
 * 
 * @param originalProps 원본 props
 * @returns 변환된 리치 에디터 컴포넌트 props
 */
export function adaptRichEditorProps(originalProps: any): RichEditorProps {
  // 기본값 설정
  const originalContent = originalProps?.content || '';
  const originalComponentProps = originalProps?.componentProps || {};
  
  // 콘텐츠 추출 (여러 경로에서 시도)
  const content = originalProps?.content || 
                  originalComponentProps?.content || 
                  '';
  
  // 미리보기 모드 확인
  const isPreview = originalProps?.isPreview !== undefined ? originalProps.isPreview : 
                   originalComponentProps?.isPreview !== undefined ? originalComponentProps.isPreview : 
                   false;
  
  // 스타일 병합
  const style = {
    ...(originalProps?.style || {}),
    ...(originalComponentProps?.style || {})
  };
  
  // className 설정
  const className = originalProps?.className || originalComponentProps?.className || '';

  // 변환된 props 반환
  const adaptedProps: RichEditorProps = {
    id: originalProps?.id || '',
    content,
    isPreview,
    style,
    className,
    componentProps: {
      ...originalComponentProps,
      content,
      isPreview,
      style
    }
  };

  // 원본 props의 모든 속성 중 이미 처리한 속성 제외하고 나머지 복사
  const excludeKeys = ['id', 'content', 'isPreview', 'style', 'className', 'componentProps'];
  
  Object.keys(originalProps).forEach(key => {
    if (!excludeKeys.includes(key)) {
      adaptedProps[key] = originalProps[key];
    }
  });

  return adaptedProps;
}

/**
 * 에디터용 props 변환 어댑터
 * 
 * @param originalProps 원본 props
 * @returns 변환된 에디터 컴포넌트 props
 */
export function adaptRichEditorEditorProps(originalProps: any): RichEditorEditorProps {
  // 기본 props 변환
  const adaptedProps = adaptRichEditorProps(originalProps);
  
  // 에디터 관련 추가 props
  return {
    ...adaptedProps,
    isSelected: originalProps.isSelected || false,
    viewMode: originalProps.viewMode || 'pc',
    updateComponent: originalProps.updateComponent || (() => {})
  };
} 